import React, { useState, useEffect, useRef } from 'react';

interface PinKeypadProps {
  staffName: string;
  staffInitials: string;
  staffColour: string;
  onSubmit: (pin: string) => Promise<void>;
  onCancel: () => void;
  /** Called after MAX_ATTEMPTS wrong PINs. Defaults to onCancel if not provided. */
  onLockout?: () => void;
  /**
   * Self-service "Forget PIN" — when provided, shows a "Forgot PIN?" link
   * that, on confirmation, invokes this handler. Parent is responsible for
   * nulling the PIN server-side and signing the device out.
   */
  onForgetPin?: () => Promise<void>;
  cancelLabel?: string;
  error?: string | null;
  hint?: string;
  /**
   * Whether clicking the dimmed backdrop invokes onCancel. Defaults to false:
   * on the idle lock, onCancel navigates to /staff-select, so a stray click
   * would lose the user's place in the app. Opt in where dismissal is cheap
   * (StaffSelect just returns to the staff tiles).
   */
  dismissOnBackdrop?: boolean;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];
const MAX_DIGITS = 4;
const MAX_ATTEMPTS = 3;

const STYLES = `
@keyframes pin-shake {
  0%   { transform: translateX(0); }
  20%  { transform: translateX(-10px); }
  40%  { transform: translateX(10px); }
  60%  { transform: translateX(-7px); }
  80%  { transform: translateX(7px); }
  100% { transform: translateX(0); }
}
.pin-shake { animation: pin-shake 0.4s ease; }

/* Keypad chrome sits on the app's warm ivory paper, not the cool blue-grey
   this component originally shipped with. The card behind it is ivory, and
   grey keys on ivory read as dead rather than as neutral — every tint here
   (key faces, digits, shadows, focus ring) is drawn from the forest/ivory
   palette so the overlay looks like the rest of the app. */
.pin-key {
  position: relative;
  overflow: hidden;
  width: 64px; height: 64px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(160deg, #fffefa 0%, #ebe5d5 100%);
  color: #14321a;
  font-size: 1.4rem;
  font-weight: 600;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  touch-action: manipulation;
  transition:
    transform 0.09s cubic-bezier(.2,.7,.3,1.3),
    box-shadow 0.12s ease,
    background 0.12s ease;
  box-shadow:
    0 1px 2px rgba(13,77,26,0.07),
    0 3px 10px rgba(13,77,26,0.08),
    inset 0 -1px 0 rgba(20,58,32,0.06),
    inset 0 1px 0 rgba(255,255,255,0.9);
}
.pin-key.is-backspace {
  background: linear-gradient(160deg, #fdf3f1 0%, #f2d9d4 100%);
  color: #b8302f;
  font-size: 1.1rem;
  font-weight: 400;
}
@media (hover: hover) {
  .pin-key:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow:
      0 2px 4px rgba(13,77,26,0.10),
      0 8px 18px rgba(13,77,26,0.14),
      inset 0 -1px 0 rgba(20,58,32,0.06),
      inset 0 1px 0 rgba(255,255,255,0.95);
  }
}
.pin-key:active:not(:disabled) {
  transform: scale(0.9) translateY(1px);
  box-shadow:
    0 1px 2px rgba(13,77,26,0.05),
    inset 0 2px 5px rgba(20,58,32,0.12);
}
.pin-key:disabled {
  cursor: default;
  opacity: 0.4;
}
.pin-key:focus-visible {
  outline: 2px solid rgba(38,146,63,0.6);
  outline-offset: 2px;
}

.pin-key-flash {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(circle, var(--flash-color, rgba(0,0,0,0.18)) 0%, transparent 65%);
  animation: pin-flash 0.36s ease-out forwards;
}
@keyframes pin-flash {
  0%   { opacity: 0.9; transform: scale(0.5); }
  100% { opacity: 0;   transform: scale(1.55); }
}

@keyframes pin-dot-pop {
  0%   { transform: scale(0.4); }
  55%  { transform: scale(1.35); }
  100% { transform: scale(1); }
}
.pin-dot { transition: background 0.15s, border-color 0.15s, box-shadow 0.15s; }
.pin-dot.is-popping { animation: pin-dot-pop 0.3s cubic-bezier(.5,1.6,.4,1); }
`;

const PinKeypad: React.FC<PinKeypadProps> = ({
  staffName, staffInitials, staffColour,
  onSubmit, onCancel, onLockout, onForgetPin,
  cancelLabel = 'Switch user', error, hint,
  dismissOnBackdrop = false,
}) => {
  const [digits, setDigits] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [isShaking, setIsShaking] = useState(false);
  const [forgetting, setForgetting] = useState(false);
  const [pressEvent, setPressEvent] = useState<{ key: string; ts: number } | null>(null);
  const [poppingDot, setPoppingDot] = useState(-1);
  const prevDigitsLen = useRef(0);

  useEffect(() => { if (digits.length > 0) setLocalError(null); }, [digits]);

  // Pop the newly-filled dot whenever digits grows
  useEffect(() => {
    if (digits.length > prevDigitsLen.current) {
      const idx = digits.length - 1;
      setPoppingDot(idx);
      const t = window.setTimeout(() => setPoppingDot(-1), 320);
      prevDigitsLen.current = digits.length;
      return () => window.clearTimeout(t);
    }
    prevDigitsLen.current = digits.length;
  }, [digits.length]);

  const triggerShake = () => {
    setIsShaking(false);
    requestAnimationFrame(() => setIsShaking(true));
    setTimeout(() => setIsShaking(false), 450);
  };

  /** Immediate visual + haptic feedback. Fired on pointerdown / keydown — no key logic. */
  const handlePress = (key: string) => {
    if (loading || attempts >= MAX_ATTEMPTS || key === '') return;
    setPressEvent({ key, ts: performance.now() });
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(8);
    }
  };

  const handleKey = async (key: string) => {
    if (loading) return;
    if (key === '⌫') { setDigits(d => d.slice(0, -1)); return; }
    if (key === '') return;
    const next = digits + key;
    if (next.length > MAX_DIGITS) return;
    setDigits(next);
    if (next.length === MAX_DIGITS) await submit(next);
  };

  const submit = async (pin: string) => {
    setLoading(true);
    setLocalError(null);
    try {
      await onSubmit(pin);
    } catch {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setDigits('');
      triggerShake();
      if (newAttempts >= MAX_ATTEMPTS) {
        setLocalError('Too many incorrect attempts. Signing out…');
        setTimeout(() => (onLockout ?? onCancel)(), 1500);
      } else {
        const remaining = MAX_ATTEMPTS - newAttempts;
        setLocalError(`Incorrect PIN — ${remaining} ${remaining === 1 ? 'try' : 'tries'} left.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Keep refs so the keyboard handler always sees the latest closures without re-registering
  const handleKeyRef = useRef(handleKey);
  const handlePressRef = useRef(handlePress);
  handleKeyRef.current = handleKey;
  handlePressRef.current = handlePress;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        handlePressRef.current(e.key);
        handleKeyRef.current(e.key);
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        handlePressRef.current('⌫');
        handleKeyRef.current('⌫');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const displayError = localError || error;
  const locked = attempts >= MAX_ATTEMPTS;

  return (
    <>
      <style>{STYLES}</style>
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(15,15,25,0.6)',
          backdropFilter: 'blur(12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        onClick={dismissOnBackdrop ? onCancel : undefined}
      >
        <div
          className={isShaking ? 'pin-shake' : ''}
          style={{
            // The app's ivory paper, but the *opaque* mix rather than the
            // shared .ivory-panel class. That class is translucent (0.96 →
            // 0.88), which works over Bootstrap's light backdrop but goes
            // visibly dingy over this overlay's dark blurred scrim.
            background: 'linear-gradient(180deg, #fffefa 0%, #f5f1e4 100%)',
            borderRadius: 24,
            width: 320,
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
            overflow: 'hidden',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Identity header */}
          <div
            style={{
              background: `linear-gradient(135deg, ${staffColour}22 0%, ${staffColour}08 100%)`,
              borderBottom: '1px solid rgba(20,58,32,0.10)',
              padding: '1.5rem 1.25rem 1.25rem',
              position: 'relative',
              textAlign: 'center',
            }}
          >
            {/* Switch user / cancel button — top right */}
            <button
              onClick={onCancel}
              style={{
                position: 'absolute', top: 12, right: 12,
                display: 'flex', alignItems: 'center', gap: 5,
                background: 'rgba(20,58,32,0.07)',
                border: 'none',
                borderRadius: 20,
                padding: '5px 10px 5px 8px',
                fontSize: '0.72rem',
                fontWeight: 600,
                color: '#4a5b48',
                cursor: 'pointer',
                letterSpacing: '0.01em',
              }}
            >
              <i className="fas fa-exchange-alt" style={{ fontSize: '0.7rem' }} />
              {cancelLabel}
            </button>

            {/* Avatar */}
            <div
              style={{
                width: 68, height: 68, borderRadius: '50%',
                background: staffColour,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 0.75rem',
                fontSize: '1.4rem', fontWeight: 700, color: '#fff',
                boxShadow: `0 4px 16px ${staffColour}55`,
              }}
            >
              {staffInitials}
            </div>

            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#14321a', marginBottom: 2 }}>
              {staffName}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#7e8a78' }}>Enter your PIN</div>

            {hint && (
              <div style={{
                marginTop: '0.6rem',
                fontSize: '0.75rem', color: '#a86a00', fontWeight: 500,
                background: 'rgba(168,106,0,0.08)', borderRadius: 8, padding: '5px 10px',
              }}>
                {hint}
              </div>
            )}
          </div>

          <div style={{ padding: '1.25rem 1.5rem 1.5rem' }}>

            {/* Dot indicators */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.9rem', marginBottom: '1rem' }}>
              {Array.from({ length: MAX_DIGITS }).map((_, i) => {
                const filled = i < digits.length;
                return (
                  <div
                    key={i}
                    className={`pin-dot${poppingDot === i ? ' is-popping' : ''}`}
                    style={{
                      width: 13, height: 13, borderRadius: '50%',
                      background: filled ? staffColour : 'transparent',
                      border: `2px solid ${filled ? staffColour : 'rgba(20,58,32,0.22)'}`,
                      boxShadow: filled ? `0 0 6px ${staffColour}66` : 'none',
                    }}
                  />
                );
              })}
            </div>

            {/* Error */}
            {displayError && (
              <div style={{
                color: '#b8302f', fontSize: '0.8rem', textAlign: 'center',
                marginBottom: '0.75rem',
                background: 'rgba(184,48,47,0.08)', borderRadius: 8, padding: '6px 10px',
              }}>
                {displayError}
              </div>
            )}

            {/* Keys — circular phone-style */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' }}>
              {KEYS.map((key, i) => {
                const isBackspace = key === '⌫';
                const isEmpty = key === '';
                const flashColor = isBackspace ? 'rgba(220,53,69,0.32)' : `${staffColour}66`;
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'center' }}>
                    {!isEmpty && (
                      <button
                        className={`pin-key${isBackspace ? ' is-backspace' : ''}`}
                        onPointerDown={() => handlePress(key)}
                        onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') handlePress(key); }}
                        onClick={() => handleKey(key)}
                        disabled={loading || locked}
                        aria-label={isBackspace ? 'Delete' : `Digit ${key}`}
                      >
                        {key}
                        {pressEvent?.key === key && (
                          <span
                            key={pressEvent.ts}
                            className="pin-key-flash"
                            style={{ ['--flash-color' as any]: flashColor }}
                          />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {onForgetPin && (
              <div style={{ marginTop: '0.9rem', textAlign: 'center' }} data-help="account.forgot-pin">
                <button
                  type="button"
                  // Straight to the re-auth screen, which is the only place
                  // the PIN is actually cleared. The "are you sure?" that used
                  // to sit here could not do the thing it warned about, and the
                  // screen it opened already states the consequence and demands
                  // a password — a far stronger gate than a second click.
                  onClick={() => { setForgetting(true); void onForgetPin().finally(() => setForgetting(false)); }}
                  disabled={loading || locked || forgetting}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#7e8a78',
                    fontSize: '0.78rem',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: 4,
                  }}
                >
                  Forgot your PIN?
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PinKeypad;
